import type { IconType } from 'react-icons';
import {
    LuHouse, LuLayoutDashboard, LuTarget, LuListTodo, LuCalendar, LuMessageSquare,
    LuKanban, LuContact, LuBuilding2, LuNotebookPen, LuFileText, LuSend, LuMegaphone,
    LuLink, LuChartNoAxesColumn, LuCreditCard,
} from 'react-icons/lu';
import { getUserPermissions, hasPermission, temFuncionalidade } from './permissions';

/**
 * O mapa da navegação de OPERAÇÃO — o que a sidebar mostra no computador e o
 * drawer mostra no celular.
 *
 * Esta lista é a ÚNICA fonte de verdade das duas, pelo mesmo motivo de
 * `configuracoes.ts`: enquanto o drawer teve a própria lista escrita à mão,
 * ele ficou com sete destinos enquanto a sidebar tinha vinte — quem entrava
 * pelo celular não alcançava Contatos, Tarefas, Relatórios nem nada de
 * Configurações. Tela nova entra aqui uma vez e aparece nos dois lugares.
 *
 * O que NÃO mora aqui, de propósito:
 *   • as seções recolhíveis de conteúdo dinâmico (as conversas do Assistente),
 *     que são dados, não navegação;
 *   • a área de Configurações, que já tem o mapa dela em `configuracoes.ts`.
 */

export interface ItemNav {
    rotulo: string;
    to: string;
    icone: IconType;
    /** Id no DOM — o checklist de Primeiros passos aponta para alguns. */
    id?: string;
    /** Nenhuma = todo perfil vê. Mais de uma = basta ter qualquer delas. */
    permissoes?: string[];
    /** Chave de Sistema › Funcionalidades; desligada, o item some. */
    funcionalidade?: string;
    /** Contador do `GlobalNotificationContext`, quando o item tem um. */
    badge?: 'tarefasVencidas' | 'mensagensNaoLidas';
}

export interface GrupoNav {
    /** Sem título = o bloco principal, sem cabeçalho. */
    titulo?: string;
    /** Chave da seção recolhível na sidebar. */
    chave?: 'marketing';
    itens: ItemNav[];
}

export const GRUPOS_OPERACAO: GrupoNav[] = [
    {
        itens: [
            /* A Home não tem permissão: é a porta de entrada de todo perfil, e o
               alcance do dado é decidido na API. */
            { rotulo: 'Home', to: '/home', icone: LuHouse },
            { rotulo: 'Painel Operacional', to: '/dashboard', icone: LuLayoutDashboard, permissoes: ['painel_operacional'] },
            { rotulo: 'Gestão de vendas', to: '/monitoramento', icone: LuTarget, permissoes: ['gestao_vendas', 'gestao_vendas_vendedor'] },
            /* Tarefas não tem permissão: são as tarefas de quem está logado.
               O número é o de vencidas — a única contagem que pede pressa. */
            { rotulo: 'Tarefas', to: '/tarefas', icone: LuListTodo, badge: 'tarefasVencidas' },
            { rotulo: 'Agenda', to: '/agenda', icone: LuCalendar, permissoes: ['agenda'] },
            { rotulo: 'Chat', to: '/chat', icone: LuMessageSquare, id: 'sidebar-chat', permissoes: ['chat'], badge: 'mensagensNaoLidas' },
            { rotulo: 'Oportunidades', to: '/pipeline', icone: LuKanban, id: 'sidebar-pipeline', permissoes: ['fluxo_vendas'] },
            { rotulo: 'Contatos', to: '/contacts', icone: LuContact, id: 'sidebar-contacts', permissoes: ['contatos'] },
            { rotulo: 'Organizações', to: '/organizations', icone: LuBuilding2, id: 'sidebar-organizations', permissoes: ['contatos'] },
            /* Pede as duas coisas, como Anúncios: a funcionalidade ligada para a
               empresa e a permissão do perfil. Quem não vende recorrência não vê
               item novo no menu. */
            { rotulo: 'Assinaturas', to: '/assinaturas', icone: LuCreditCard, id: 'sidebar-assinaturas', permissoes: ['assinaturas'], funcionalidade: 'assinaturas' },
            /* Sem permissão: `anotacoes` ainda não existe na base e o caderno é
               pessoal — ver PUBLIC_ROUTES em config/permissions.ts. */
            { rotulo: 'Anotações', to: '/anotacoes', icone: LuNotebookPen },
            /* Relatórios é UM item: os relatórios são conteúdo da tela, não itens
               de menu — se cada um virasse item, o menu cresceria com o uso. */
            { rotulo: 'Relatórios', to: '/relatorios', icone: LuFileText, permissoes: ['relatorio_vendas', 'relatorio_campanhas'] },
        ],
    },
    {
        titulo: 'Marketing',
        chave: 'marketing',
        itens: [
            { rotulo: 'Disparador', to: '/campanhas', icone: LuSend, permissoes: ['marketing_disparador'] },
            { rotulo: 'Templates', to: '/campaign-templates', icone: LuMegaphone, permissoes: ['marketing_templates'] },
            /* Saiu da terceira aba de Gerenciar Empresa (2026-09-10). Mantém a
               permissão que já tinha lá — `dias_funcionamento`. */
            { rotulo: 'Página de captura', to: '/pagina-de-captura', icone: LuLink, permissoes: ['dias_funcionamento'] },
            /* A quarta porta de entrada de lead. Mora em Marketing, e não em
               Configurações, porque quem paga mídia procura o número aqui — a
               conexão da conta continua em Conexões. Pede as duas coisas: a
               funcionalidade ligada para a empresa e a permissão do perfil. */
            { rotulo: 'Anúncios', to: '/anuncios', icone: LuChartNoAxesColumn, permissoes: ['anuncios'], funcionalidade: 'anuncios' },
        ],
    },
];

/** O item aparece para quem está logado? Permissão E funcionalidade. */
export const podeVerItem = (item: ItemNav, permissoes = getUserPermissions()): boolean => {
    if (item.funcionalidade && !temFuncionalidade(item.funcionalidade)) return false;
    if (!item.permissoes || item.permissoes.length === 0) return true;
    return hasPermission(permissoes, item.permissoes);
};

/** Os itens de um grupo que a pessoa alcança; grupo vazio não deve ser desenhado. */
export const itensVisiveis = (grupo: GrupoNav, permissoes = getUserPermissions()): ItemNav[] =>
    grupo.itens.filter(i => podeVerItem(i, permissoes));
